// src/pages/RegisterRestaurant.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {  Check, Info,  Camera, FileText, Clock, Map, User, Star, Coffee, Utensils, ArrowRight } from 'lucide-react';

const RegisterRestaurant = () => {
  const [formData, setFormData] = useState({
    restaurantName: '',
    ownerName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    cuisine: '',
    description: '',
    website: '',
    priceRange: 'medium',
    capacity: '',
    openingHours: {
      monday: { open: '09:00', close: '22:00', closed: false },
      tuesday: { open: '09:00', close: '22:00', closed: false },
      wednesday: { open: '09:00', close: '22:00', closed: false },
      thursday: { open: '09:00', close: '22:00', closed: false },
      friday: { open: '09:00', close: '23:00', closed: false },
      saturday: { open: '09:00', close: '23:00', closed: false },
      sunday: { open: '09:00', close: '22:00', closed: false }
    },
    features: {
      halalCertified: false,
      vegetarianOptions: false,
      outdoorSeating: false,
      delivery: false,
      takeout: false,
      wifi: false,
      parking: false,
      familyFriendly: false,
      reservations: false
    }
  });

  const [images, setImages] = useState([]);
  const [menuFile, setMenuFile] = useState(null);
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [animation, setAnimation] = useState(false);

  const cuisineTypes = [
    'Middle Eastern', 'Mediterranean', 'Turkish', 'Lebanese', 'Moroccan', 
    'Indian', 'Pakistani', 'Italian', 'French', 'American', 'Mexican', 
    'Japanese', 'Chinese', 'Thai', 'Vietnamese', 'Other'
  ];

  useEffect(() => {
    // Trigger animation when step changes
    setAnimation(true);
    const timer = setTimeout(() => {
      setAnimation(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [step]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFeatureChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [name]: checked
      }
    }));
  };

  const handleHoursChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      openingHours: {
        ...prev.openingHours,
        [day]: {
          ...prev.openingHours[day],
          [field]: field === 'closed' ? !prev.openingHours[day].closed : value
        }
      }
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Simulate image preview
      const newImages = files.map(file => ({
        name: file.name,
        preview: URL.createObjectURL(file),
        file
      }));
      setImages(prev => [...prev, ...newImages]);
    }
  };

  const handleMenuUpload = (e) => {
    if (e.target.files[0]) {
      setMenuFile({
        name: e.target.files[0].name,
        file: e.target.files[0]
      });
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeMenu = () => {
    setMenuFile(null);
  };

  const validateStep = (currentStep) => {
    let isValid = true;
    const newErrors = {};

    if (currentStep === 1) {
      if (!formData.restaurantName.trim()) {
        newErrors.restaurantName = 'Restaurant name is required';
        isValid = false;
      }
      if (!formData.ownerName.trim()) {
        newErrors.ownerName = 'Owner name is required';
        isValid = false;
      }
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
        isValid = false;
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
        isValid = false;
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
        isValid = false;
      }
    }

    if (currentStep === 2) {
      if (!formData.address.trim()) {
        newErrors.address = 'Address is required';
        isValid = false;
      }
      if (!formData.city.trim()) {
        newErrors.city = 'City is required';
        isValid = false;
      }
      if (!formData.postalCode.trim()) {
        newErrors.postalCode = 'Postal code is required';
        isValid = false;
      }
      if (!formData.country.trim()) {
        newErrors.country = 'Country is required';
        isValid = false;
      }
    }

    if (currentStep === 3) {
      if (!formData.cuisine) {
        newErrors.cuisine = 'Cuisine type is required';
        isValid = false;
      }
      if (!formData.description.trim()) {
        newErrors.description = 'Description is required';
        isValid = false;
      } else if (formData.description.length < 50) {
        newErrors.description = 'Description should be at least 50 characters';
        isValid = false;
      }
      if (!formData.capacity.trim()) {
        newErrors.capacity = 'Seating capacity is required';
        isValid = false;
      } else if (isNaN(formData.capacity)) {
        newErrors.capacity = 'Seating capacity must be a number';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateStep(step)) {
      setIsSubmitting(true);
      
      // Simulate API call
      setTimeout(() => {
        setIsSubmitting(false);
        setSubmitSuccess(true);
        // In a real app, you'd submit to your backend here
        console.log('Form Data:', formData);
        console.log('Images:', images);
        console.log('Menu File:', menuFile);
      }, 1500);
    }
  };

  if (submitSuccess) {
    return (
      <div className="success-page">
        <div className="success-banner">
          <div className="success-overlay"></div>
          <div className="container py-5 position-relative">
            <div className="row justify-content-center text-center">
              <div className="col-lg-10">
                <div className="animated-checkmark mb-4">
                  <svg width="120" height="120" viewBox="0 0 120 120">
                    <circle className="checkmark-circle" cx="60" cy="60" r="54" fill="none" stroke="#FFF" strokeWidth="7" />
                    <path className="checkmark-check" d="M35 60 L50 75 L85 40" fill="none" stroke="#FFF" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h1 className="text-white fw-bold display-4 mb-3">Registration Successful!</h1>
                <p className="text-white lead mb-0">
                  Your restaurant is now being reviewed by our team
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-xl-7">
              <div className="card border-0 shadow-lg success-card">
                <div className="success-restaurant-images">
                  <div className="image-grid">
                    <div className="main-image">
                      <img src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2074" alt="Restaurant" />
                    </div>
                    <div className="side-images">
                      <img src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070" alt="Restaurant" />
                      <img src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2070" alt="Restaurant" />
                    </div>
                  </div>
                </div>

                <div className="card-body p-5">
                  <div className="success-details text-center">
                    <h2 className="fw-bold mb-4">What Happens Next?</h2>
                    
                    <div className="next-steps">
                      <div className="next-step-item">
                        <div className="step-number">1</div>
                        <div className="step-content">
                          <h5>Review Process</h5>
                          <p>Our team will review your restaurant details within 2-3 business days</p>
                        </div>
                      </div>
                      
                      <div className="next-step-item">
                        <div className="step-number">2</div>
                        <div className="step-content">
                          <h5>Account Setup</h5>
                          <p>Once approved, we'll help set up your restaurant's online presence</p>
                        </div>
                      </div>
                      
                      <div className="next-step-item">
                        <div className="step-number">3</div>
                        <div className="step-content">
                          <h5>Start Receiving Orders</h5>
                          <p>Your restaurant will be live on our platform and ready to welcome new customers</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="success-actions mt-5">
                      <Link to="/" className="btn btn-primary btn-lg px-5 me-3 shadow-sm">
                        Return to Home
                      </Link>
                      <Link to="/restaurants" className="btn btn-outline-primary btn-lg px-5">
                        Explore Restaurants
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getStepIcon = (stepNumber) => {
    switch(stepNumber) {
      case 1: return <User size={24} />;
      case 2: return <Map size={24} />;
      case 3: return <Utensils size={24} />;
      case 4: return <Camera size={24} />;
      default: return <User size={24} />;
    }
  };

  return (
    <div className="register-restaurant-page">
      {/* Hero Banner */}
      <div className="registration-banner">
        <div className="banner-overlay"></div>
        <div className="container py-5 position-relative">
          <div className="row align-items-center">
            <div className="col-lg-6 text-white">
              <h1 className="fw-bold display-4 mb-3 hero-title">Join Our Culinary Community</h1>
              <p className="lead mb-4 hero-subtitle">Register your restaurant and reach thousands of customers looking for authentic halal dining experiences.</p>
              <div className="d-flex flex-wrap gap-3 mb-4">
                <div className="banner-stat">
                  <div className="stat-icon">
                    <User size={24} />
                  </div>
                  <h3 className="mb-0">5000+</h3>
                  <p className="mb-0">Monthly Visitors</p>
                </div>
                <div className="banner-stat">
                  <div className="stat-icon">
                    <Utensils size={24} />
                  </div>
                  <h3 className="mb-0">500+</h3>
                  <p className="mb-0">Restaurants</p>
                </div>
                <div className="banner-stat">
                  <div className="stat-icon">
                    <Star size={24} />
                  </div>
                  <h3 className="mb-0">4.8</h3>
                  <p className="mb-0">Average Rating</p>
                </div>
              </div>
              <div className="badge-strip">
                <span>Free Registration</span>
                <span>No Hidden Fees</span>
                <span>24/7 Support</span>
              </div>
            </div>
            
            <div className="col-lg-6 d-none d-lg-block">
              <div className="floating-images">
                <div className="image-container">
                  <img src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070" alt="Restaurant" className="floating-img img-1" />
                  <img src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2074" alt="Restaurant" className="floating-img img-2" />
                  <img src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2070" alt="Restaurant" className="floating-img img-3" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-5">
        <div className="row justify-content-center mb-5">
          <div className="col-xl-10">
            {/* Registration Steps Indicators */}
            <div className="registration-steps mb-5">
              <div className="registration-progress-container">
                <div className="registration-progress-bar" style={{ width: `${(step / 4) * 100}%` }}></div>
              </div>
              <div className="registration-step-circles">
                {[1, 2, 3, 4].map(stepNumber => (
                  <div 
                    key={stepNumber}
                    className={`step-circle ${stepNumber === step ? 'active' : stepNumber < step ? 'completed' : ''}`}
                    onClick={() => stepNumber < step && setStep(stepNumber)}
                  >
                    {stepNumber < step ? <Check size={18} /> : stepNumber}
                    <div className="step-label">
                      {stepNumber === 1 ? 'Basic Info' : 
                       stepNumber === 2 ? 'Location' : 
                       stepNumber === 3 ? 'Details' : 'Media'}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-card-container">
              <div className="card border-0 shadow-lg form-card">
                <div className="row g-0">
                  <div className="col-lg-4 d-none d-lg-block">
                    <div className="h-100 registration-sidebar">
                      <div className="sidebar-gradient"></div>
                      <div className="sidebar-content " >
                        <div className="step-icon-large">
                          {getStepIcon(step)}
                        </div>
                        <h2 className="fw-bold mb-3">
                          {step === 1 ? 'Tell us about your restaurant' : 
                           step === 2 ? 'Where are you located?' : 
                           step === 3 ? 'What makes you special?' : 
                           'Show off your restaurant'}
                        </h2>
                        <p className="opacity-75 mb-4">
                          {step === 1 ? 'Start by providing basic information about your restaurant and contact details.' : 
                           step === 2 ? 'Help customers find you by providing your exact location.' : 
                           step === 3 ? 'Tell us about your cuisine, features, and when you\'re open.' : 
                           'Upload high-quality photos to attract more customers.'}
                        </p>
                        
                        <div className="sidebar-testimonial">
                          <div className="testimonial-text">
                            {step === 1 ? "Joining this platform was one of the best business decisions I've made. Our reservations increased by 30% in just two months!" : 
                             step === 2 ? "The map integration helped new customers find our restaurant easily. It's been a game-changer for our visibility." : 
                             step === 3 ? "Being able to showcase our Halal certification and special features helped us attract our target audience effectively." : 
                             "The photo gallery really makes our restaurant stand out. Customers often mention they came because of our attractive photos!"}
                          </div>
                          <div className="testimonial-author">
                            <div className="author-avatar">
                              {step === 1 ? "MA" : step === 2 ? "SK" : step === 3 ? "LR" : "JT"}
                            </div>
                            <div className="author-info">
                              <div className="author-name">
                                {step === 1 ? "Mohammed Ahmed" : step === 2 ? "Sarah Khan" : step === 3 ? "Layla Rahman" : "Jamal Tariq"}
                              </div>
                              <div className="author-role">
                                {step === 1 ? "Owner, Al-Madina Grill" : step === 2 ? "Manager, Spice Village" : step === 3 ? "Chef, Lebanese Paradise" : "Owner, Tandoor House"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-8">
                    <div className="card-body p-4 p-lg-5">
                      <div className={`form-container ${animation ? 'fade-animation' : ''}`}>
                        <form onSubmit={handleSubmit}>
                          {/* Step 1: Basic Information */}
                          {step === 1 && (
                            <div className="step-content">
                              <div className="step-header">
                                <h3 className="fw-bold mb-1">Basic Information</h3>
                                <p className="text-muted mb-4">Let's start with the essentials about your restaurant</p>
                              </div>
                              
                              <div className="form-floating mb-4">
                                <input
                                  type="text"
                                  className={`form-control ${errors.restaurantName ? 'is-invalid' : ''}`}
                                  id="restaurantName"
                                  name="restaurantName"
                                  placeholder="Restaurant Name"
                                  value={formData.restaurantName}
                                  onChange={handleInputChange}
                                />
                                <label htmlFor="restaurantName">Restaurant Name *</label>
                                {errors.restaurantName && <div className="invalid-feedback">{errors.restaurantName}</div>}
                              </div>
                              
                              <div className="form-floating mb-4">
                                <input
                                  type="text"
                                  className={`form-control ${errors.ownerName ? 'is-invalid' : ''}`}
                                  id="ownerName"
                                  name="ownerName"
                                  placeholder="Owner/Manager Name"
                                  value={formData.ownerName}
                                  onChange={handleInputChange}
                                />
                                <label htmlFor="ownerName">Owner/Manager Name *</label>
                                {errors.ownerName && <div className="invalid-feedback">{errors.ownerName}</div>}
                              </div>
                              
                              <div className="form-floating mb-4">
                                <input
                                  type="email"
                                  className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                  id="email"
                                  name="email"
                                  placeholder="Email Address"
                                  value={formData.email}
                                  onChange={handleInputChange}
                                />
                                <label htmlFor="email">Email Address *</label>
                                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                              </div>
                              
                              <div className="form-floating mb-4">
                                <input
                                  type="tel"
                                  className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                                  id="phone"
                                  name="phone"
                                  placeholder="Phone Number"
                                  value={formData.phone}
                                  onChange={handleInputChange}
                                />
                                <label htmlFor="phone">Phone Number *</label>
                                {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                              </div>
                              
                              <div className="form-floating mb-4">
                                <input
                                  type="text"
                                  className="form-control"
                                  id="website"
                                  name="website"
                                  placeholder="Website (optional)"
                                  value={formData.website}
                                  onChange={handleInputChange}
                                />
                                <label htmlFor="website">Website (optional)</label>
                              </div>

                              <div className="info-callout">
                                <div className="callout-icon">
                                  <Info size={24} />
                                </div>
                                <div className="callout-content">
                                  <h5>Benefits of Registration</h5>
                                  <ul className="callout-list">
                                    <li>Increased visibility to thousands of potential customers</li>
                                    <li>Fully manageable restaurant profile</li>
                                    <li>Seamless online reservation system</li>
                                    <li>Insights and analytics on customer preferences</li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Step 2: Location Information */}
                          {step === 2 && (
                            <div className="step-content">
                              <div className="step-header">
                                <h3 className="fw-bold mb-1">Location Information</h3>
                                <p className="text-muted mb-4">Help customers find your restaurant</p>
                              </div>
                              
                              <div className="form-floating mb-4">
                                <input
                                  type="text"
                                  className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                                  id="address"
                                  name="address"
                                  placeholder="Street Address"
                                  value={formData.address}
                                  onChange={handleInputChange}
                                />
                                <label htmlFor="address">Street Address *</label>
                                {errors.address && <div className="invalid-feedback">{errors.address}</div>}
                              </div>
                              
                              <div className="row">
                                <div className="col-md-6 mb-4">
                                  <div className="form-floating">
                                    <input
                                      type="text"
                                      className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                                      id="city"
                                      name="city"
                                      placeholder="City"
                                      value={formData.city}
                                      onChange={handleInputChange}
                                    />
                                    <label htmlFor="city">City *</label>
                                    {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                                  </div>
                                </div>
                                
                                <div className="col-md-6 mb-4">
                                  <div className="form-floating">
                                    <input
                                      type="text"
                                      className={`form-control ${errors.postalCode ? 'is-invalid' : ''}`}
                                      id="postalCode"
                                      name="postalCode"
                                      placeholder="Postal Code"
                                      value={formData.postalCode}
                                      onChange={handleInputChange}
                                    />
                                    <label htmlFor="postalCode">Postal Code *</label>
                                    {errors.postalCode && <div className="invalid-feedback">{errors.postalCode}</div>}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="form-floating mb-4">
                                <input
                                  type="text"
                                  className={`form-control ${errors.country ? 'is-invalid' : ''}`}
                                  id="country"
                                  name="country"
                                  placeholder="Country"
                                  value={formData.country}
                                  onChange={handleInputChange}
                                />
                                <label htmlFor="country">Country *</label>
                                {errors.country && <div className="invalid-feedback">{errors.country}</div>}
                              </div>
                              
                              <div className="location-preview mb-4">
                                <div className="map-container">
                                  <img 
                                    src="https://images.unsplash.com/photo-1569336415962-a4bd9f69cd83?q=80&w=2071" 
                                    alt="Map Preview" 
                                    className="map-image"
                                  />
                                  <div className="map-pin">
                                    <div className="pin"></div>
                                    <div className="pin-shadow"></div>
                                  </div>
                                </div>
                                <div className="map-footer">
                                  <p className="mb-0">
                                    <Info size={16} className="me-2" />
                                    Your restaurant will appear on our interactive map for easy discovery
                                  </p>
                                </div>
                              </div>
                              
                              <div className="nearby-attractions">
                                <h5 className="mb-3">Nearby Attractions (Optional)</h5>
                                <p className="text-muted mb-3">Let customers know about notable places near your restaurant</p>
                                <div className="input-group mb-3">
                                  <input type="text" className="form-control" placeholder="e.g., 5 min from Central Park" />
                                  <button className="btn btn-outline-secondary" type="button">Add</button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Step 3: Restaurant Details */}
                          {step === 3 && (
                            <div className="step-content">
                              <div className="step-header">
                                <h3 className="fw-bold mb-1">Restaurant Details</h3>
                                <p className="text-muted mb-4">Tell us what makes your restaurant special</p>
                              </div>
                              
                              <div className="form-floating mb-4">
                                <select
                                  className={`form-select ${errors.cuisine ? 'is-invalid' : ''}`}
                                  id="cuisine"
                                  name="cuisine"
                                  value={formData.cuisine}
                                  onChange={handleInputChange}
                                  aria-label="Cuisine Type"
                                >
                                  <option value="">Select cuisine type</option>
                                  {cuisineTypes.map(cuisine => (
                                    <option key={cuisine} value={cuisine}>{cuisine}</option>
                                  ))}
                                </select>
                                <label htmlFor="cuisine">Cuisine Type *</label>
                                {errors.cuisine && <div className="invalid-feedback">{errors.cuisine}</div>}
                              </div>
                              
                              <div className="form-floating mb-4">
                                <textarea
                                  className={`form-control description-textarea ${errors.description ? 'is-invalid' : ''}`}
                                  id="description"
                                  name="description"
                                  placeholder="Restaurant Description"
                                  value={formData.description}
                                  onChange={handleInputChange}
                                  style={{ height: '150px' }}
                                ></textarea>
                                <label htmlFor="description">Restaurant Description *</label>
                                {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                                <div className="form-text mt-1">Min. 50 characters. Describe your cuisine, ambiance, and what makes your restaurant unique.</div>
                              </div>
                              
                              <div className="row">
                                <div className="col-md-6 mb-4">
                                  <div className="form-floating">
                                    <select
                                      className="form-select"
                                      id="priceRange"
                                      name="priceRange"
                                      value={formData.priceRange}
                                      onChange={handleInputChange}
                                      aria-label="Price Range"
                                    >
                                      <option value="budget">Budget ($)</option>
                                      <option value="medium">Medium ($$)</option>
                                      <option value="expensive">Expensive ($$$)</option>
                                      <option value="luxury">Luxury ($$$$)</option>
                                    </select>
                                    <label htmlFor="priceRange">Price Range</label>
                                  </div>
                                </div>
                                
                                <div className="col-md-6 mb-4">
                                  <div className="form-floating">
                                    <input
                                      type="number"
                                      className={`form-control ${errors.capacity ? 'is-invalid' : ''}`}
                                      id="capacity"
                                      name="capacity"
                                      placeholder="Seating Capacity"
                                      value={formData.capacity}
                                      onChange={handleInputChange}
                                    />
                                    <label htmlFor="capacity">Seating Capacity *</label>
                                    {errors.capacity && <div className="invalid-feedback">{errors.capacity}</div>}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="features-section mb-4">
                                <h5 className="fw-bold mb-3">Restaurant Features</h5>
                                <div className="features-grid">
                                  <div className="feature-item">
                                    <input
                                      className="form-check-input feature-checkbox"
                                      type="checkbox"
                                      id="halalCertified"
                                      name="halalCertified"
                                      checked={formData.features.halalCertified}
                                      onChange={handleFeatureChange}
                                    />
                                    <label className="form-check-label feature-label" htmlFor="halalCertified">
                                      <div className="feature-icon">
                                        <Check size={20} />
                                      </div>
                                      <span>Halal Certified</span>
                                    </label>
                                  </div>
                                  
                                  <div className="feature-item">
                                    <input
                                      className="form-check-input feature-checkbox"
                                      type="checkbox"
                                      id="vegetarianOptions"
                                      name="vegetarianOptions"
                                      checked={formData.features.vegetarianOptions}
                                      onChange={handleFeatureChange}
                                    />
                                    <label className="form-check-label feature-label" htmlFor="vegetarianOptions">
                                      <div className="feature-icon">
                                        <Coffee size={20} />
                                      </div>
                                      <span>Vegetarian Options</span>
                                    </label>
                                  </div>
                                  
                                  <div className="feature-item">
                                    <input
                                      className="form-check-input feature-checkbox"
                                      type="checkbox"
                                      id="outdoorSeating"
                                      name="outdoorSeating"
                                      checked={formData.features.outdoorSeating}
                                      onChange={handleFeatureChange}
                                    />
                                    <label className="form-check-label feature-label" htmlFor="outdoorSeating">
                                      <div className="feature-icon">
                                        <Utensils size={20} />
                                      </div>
                                      <span>Outdoor Seating</span>
                                    </label>
                                  </div>
                                  
                                  <div className="feature-item">
                                    <input
                                      className="form-check-input feature-checkbox"
                                      type="checkbox"
                                      id="delivery"
                                      name="delivery"
                                      checked={formData.features.delivery}
                                      onChange={handleFeatureChange}
                                    />
                                    <label className="form-check-label feature-label" htmlFor="delivery">
                                      <div className="feature-icon">
                                        <Utensils size={20} />
                                      </div>
                                      <span>Delivery</span>
                                    </label>
                                  </div>
                                  
                                  <div className="feature-item">
                                    <input
                                      className="form-check-input feature-checkbox"
                                      type="checkbox"
                                      id="takeout"
                                      name="takeout"
                                      checked={formData.features.takeout}
                                      onChange={handleFeatureChange}
                                    />
                                    <label className="form-check-label feature-label" htmlFor="takeout">
                                      <div className="feature-icon">
                                        <Utensils size={20} />
                                      </div>
                                      <span>Takeout</span>
                                    </label>
                                  </div>
                                  
                                  <div className="feature-item">
                                    <input
                                      className="form-check-input feature-checkbox"
                                      type="checkbox"
                                      id="wifi"
                                      name="wifi"
                                      checked={formData.features.wifi}
                                      onChange={handleFeatureChange}
                                    />
                                    <label className="form-check-label feature-label" htmlFor="wifi">
                                      <div className="feature-icon">
                                        <Utensils size={20} />
                                      </div>
                                      <span>Free WiFi</span>
                                    </label>
                                  </div>
                                  
                                  <div className="feature-item">
                                    <input
                                      className="form-check-input feature-checkbox"
                                      type="checkbox"
                                      id="parking"
                                      name="parking"
                                      checked={formData.features.parking}
                                      onChange={handleFeatureChange}
                                    />
                                    <label className="form-check-label feature-label" htmlFor="parking">
                                      <div className="feature-icon">
                                        <Utensils size={20} />
                                      </div>
                                      <span>Parking Available</span>
                                    </label>
                                  </div>
                                  
                                  <div className="feature-item">
                                    <input
                                      className="form-check-input feature-checkbox"
                                      type="checkbox"
                                      id="familyFriendly"
                                      name="familyFriendly"
                                      checked={formData.features.familyFriendly}
                                      onChange={handleFeatureChange}
                                    />
                                    <label className="form-check-label feature-label" htmlFor="familyFriendly">
                                      <div className="feature-icon">
                                        <Utensils size={20} />
                                      </div>
                                      <span>Family Friendly</span>
                                    </label>
                                  </div>
                                  
                                  <div className="feature-item">
                                    <input
                                      className="form-check-input feature-checkbox"
                                      type="checkbox"
                                      id="reservations"
                                      name="reservations"
                                      checked={formData.features.reservations}
                                      onChange={handleFeatureChange}
                                    />
                                    <label className="form-check-label feature-label" htmlFor="reservations">
                                      <div className="feature-icon">
                                        <Utensils size={20} />
                                      </div>
                                      <span>Reservations</span>
                                    </label>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="hours-section">
                                <h5 className="fw-bold mb-3">Operating Hours</h5>
                                
                                <div className="hours-accordion accordion" id="hoursAccordion">
                                  <div className="accordion-item">
                                    <h2 className="accordion-header">
                                      <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseHours" aria-expanded="true" aria-controls="collapseHours">
                                        <Clock size={18} className="me-2" /> Set Your Operating Hours
                                      </button>
                                    </h2>
                                    <div id="collapseHours" className="accordion-collapse collapse show" data-bs-parent="#hoursAccordion">
                                      <div className="accordion-body">
                                        <div className="hours-container">
                                          {Object.keys(formData.openingHours).map(day => (
                                            <div key={day} className="hours-row">
                                              <div className="day-label text-capitalize">{day}</div>
                                              
                                              <div className="hours-controls">
                                                <div className="form-check form-switch">
                                                  <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`closed-${day}`}
                                                    checked={!formData.openingHours[day].closed}
                                                    onChange={() => handleHoursChange(day, 'closed')}
                                                  />
                                                  <label className="form-check-label" htmlFor={`closed-${day}`}>
                                                    {formData.openingHours[day].closed ? 'Closed' : 'Open'}
                                                  </label>
                                                </div>
                                                
                                                {!formData.openingHours[day].closed && (
                                                  <div className="time-inputs">
                                                    <input
                                                      type="time"
                                                      className="form-control time-input"
                                                      value={formData.openingHours[day].open}
                                                      onChange={(e) => handleHoursChange(day, 'open', e.target.value)}
                                                    />
                                                    <span className="time-separator">to</span>
                                                    <input
                                                      type="time"
                                                      className="form-control time-input"
                                                      value={formData.openingHours[day].close}
                                                      onChange={(e) => handleHoursChange(day, 'close', e.target.value)}
                                                    />
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Step 4: Media Upload */}
                          {step === 4 && (
                            <div className="step-content">
                              <div className="step-header">
                                <h3 className="fw-bold mb-1">Restaurant Media</h3>
                                <p className="text-muted mb-4">Upload photos to showcase your restaurant</p>
                              </div>
                              
                              <div className="upload-section mb-5">
                                <label className="form-label fw-bold">Restaurant Photos</label>
                                <div className="upload-card dropzone mb-3">
                                  <div className="dropzone-content">
                                    <div className="upload-icon-container">
                                      <Camera size={40} className="upload-icon" />
                                    </div>
                                    <h5>Upload Restaurant Photos</h5>
                                    <p className="text-muted text-center">Drag and drop your photos here or click to browse</p>
                                    <input
                                      type="file"
                                      id="restaurantImages"
                                      className="d-none"
                                      multiple
                                      accept="image/*"
                                      onChange={handleImageUpload}
                                    />
                                    <label htmlFor="restaurantImages" className="btn btn-primary btn-lg upload-btn">
                                      Choose Photos
                                    </label>
                                  </div>
                                </div>
                                
                                {images.length > 0 && (
                                  <div className="uploaded-images">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                      <h5 className="mb-0">Uploaded Photos ({images.length})</h5>
                                      <span className="badge bg-success rounded-pill">{images.length} of 10</span>
                                    </div>
                                    <div className="image-gallery">
                                      {images.map((image, index) => (
                                        <div key={index} className="gallery-item">
                                          <div className="image-card">
                                            <img 
                                              src={image.preview} 
                                              alt={`Restaurant ${index+1}`}
                                              className="gallery-img"
                                            />
                                            <button
                                              type="button"
                                              className="btn-remove"
                                              onClick={() => removeImage(index)}
                                            >
                                              ×
                                            </button>
                                            <div className="image-overlay">
                                              <div className="image-name">{image.name}</div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                <div className="photo-tips mt-4">
                                  <h6 className="fw-bold mb-2">Photo Tips</h6>
                                  <div className="tips-grid">
                                    <div className="tip-item">
                                      <div className="tip-icon">
                                        <Check size={16} />
                                      </div>
                                      <div className="tip-text">Use landscape orientation</div>
                                    </div>
                                    <div className="tip-item">
                                      <div className="tip-icon">
                                        <Check size={16} />
                                      </div>
                                      <div className="tip-text">Show both interior and exterior</div>
                                    </div>
                                    <div className="tip-item">
                                      <div className="tip-icon">
                                        <Check size={16} />
                                      </div>
                                      <div className="tip-text">Include your best dishes</div>
                                    </div>
                                    <div className="tip-item">
                                      <div className="tip-icon">
                                        <Check size={16} />
                                      </div>
                                      <div className="tip-text">Use good lighting</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="menu-upload-section mb-4">
                                <label className="form-label fw-bold">Menu File (PDF)</label>
                                <div className="upload-card menu-dropzone mb-3">
                                  <div className="dropzone-content">
                                    <div className="upload-icon-container">
                                      <FileText size={40} className="upload-icon" />
                                    </div>
                                    <h5>Upload Menu PDF</h5>
                                    <p className="text-muted text-center">Customers can preview your menu before visiting</p>
                                    <input
                                      type="file"
                                      id="menuFile"
                                      className="d-none"
                                      accept=".pdf"
                                      onChange={handleMenuUpload}
                                    />
                                    <label htmlFor="menuFile" className="btn btn-outline-primary btn-lg upload-btn">
                                      Choose Menu File
                                    </label>
                                  </div>
                                </div>
                                
                                {menuFile && (
                                  <div className="uploaded-menu">
                                    <div className="menu-file-card">
                                      <div className="file-icon">
                                        <FileText size={24} />
                                      </div>
                                      <div className="file-info">
                                        <div className="file-name">{menuFile.name}</div>
                                        <div className="file-type">PDF Document</div>
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={removeMenu}
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              <div className="terms-section mb-4">
                                <div className="form-check terms-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="termsAgreement"
                                  />
                                  <label className="form-check-label" htmlFor="termsAgreement">
                                    I agree to the <Link to="/terms" className="text-decoration-none">Terms and Conditions</Link> and <Link to="/privacy" className="text-decoration-none">Privacy Policy</Link>
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Navigation Buttons */}
                          <div className="form-navigation mt-5">
                            {step > 1 && (
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-lg back-btn"
                                onClick={prevStep}
                              >
                                Back
                              </button>
                            )}
                            {step < 4 ? (
                              <button
                                type="button"
                                className="btn btn-primary btn-lg next-btn"
                                onClick={nextStep}
                              >
                                Continue <ArrowRight size={18} className="ms-1" />
                              </button>
                            ) : (
                              <button
                                type="submit"
                                className="btn btn-success btn-lg submit-btn"
                                disabled={isSubmitting}
                              >
                                {isSubmitting ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Submitting...
                                  </>
                                ) : (
                                  'Submit Registration'
                                )}
                              </button>
                            )}
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="registration-benefits py-5 bg-light">
        <div className="container">
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <h2 className="fw-bold mb-3">Why Register With Us?</h2>
              <p className="lead text-muted">Join our platform and enjoy these benefits</p>
            </div>
          </div>
          
          <div className="row g-4">
            <div className="col-md-4">
              <div className="benefit-card">
                <div className="benefit-icon">
                  <User size={32} />
                </div>
                <h4>More Customers</h4>
                <p>Reach thousands of customers specifically looking for halal dining options</p>
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="benefit-card">
                <div className="benefit-icon">
                  <Clock size={32} />
                </div>
                <h4>24/7 Reservations</h4>
                <p>Accept bookings anytime with our integrated reservation system</p>
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="benefit-card">
                <div className="benefit-icon">
                  <Star size={32} />
                </div>
                <h4>Verified Reviews</h4>
                <p>Build your reputation with genuine customer reviews</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS for enhanced design */}
      <style jsx>{`
        /* Banner Styles */
        .registration-banner {
          position: relative;
          background-color: #122740;
          padding: 90px 0;
          margin-bottom: 40px;
          overflow: hidden;
        }
        
        .banner-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: linear-gradient(135deg, rgba(15, 35, 65, 0.9), rgba(15, 35, 65, 0.7)), url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070');
          background-size: cover;
          background-position: center;
          z-index: 1;
        }
        
        .hero-title, .hero-subtitle {
          position: relative;
          z-index: 2;
        }
        
        .hero-title {
          animation: fadeInUp 0.6s ease-out;
        }
        
        .hero-subtitle {
          animation: fadeInUp 0.8s ease-out;
        }
        
        .banner-stat {
          position: relative;
          z-index: 2;
          background: rgba(255,255,255,0.1);
          padding: 20px;
          border-radius: 16px;
          text-align: center;
          backdrop-filter: blur(5px);
          min-width: 140px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
          transition: all 0.3s ease;
          animation: fadeInUp 1s ease-out;
        }
        
        .banner-stat:hover {
          transform: translateY(-5px);
          background: rgba(255,255,255,0.15);
        }
        
        .stat-icon {
          width: 48px;
          height: 48px;
          background: rgba(255,255,255,0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 12px;
        }
        
        .badge-strip {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          position: relative;
          z-index: 2;
          animation: fadeInUp 1.2s ease-out;
        }
        
        .badge-strip span {
          background: rgba(0,123,255,0.3);
          color: white;
          padding: 8px 16px;
          border-radius: 30px;
          font-size: 14px;
          font-weight: 500;
          backdrop-filter: blur(5px);
        }
        
        .floating-images {
          position: relative;
          height: 400px;
          z-index: 2;
        }
        
        .image-container {
          position: relative;
          width: 100%;
          height: 100%;
        }
        
        .floating-img {
          position: absolute;
          border-radius: 16px;
          box-shadow: 0 15px 35px rgba(0,0,0,0.3);
          object-fit: cover;
        }
        
        .img-1 {
          width: 280px;
          height: 200px;
          top: 0;
          left: 30px;
          z-index: 3;
          animation: float 6s ease-in-out infinite, fadeIn 1s ease-out;
        }
        
        .img-2 {
          width: 240px;
          height: 180px;
          top: 120px;
          right: 20px;
          z-index: 2;
          animation: float 8s ease-in-out infinite 1s, fadeIn 1.2s ease-out;
        }
        
        .img-3 {
          width: 200px;
          height: 150px;
          bottom: 20px;
          left: 80px;
          z-index: 1;
          animation: float 7s ease-in-out infinite 0.5s, fadeIn 1.4s ease-out;
        }

        /* Progress Steps */
        .registration-steps {
          position: relative;
          padding: 20px 0;
        }
        
        .registration-progress-container {
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 4px;
          background-color: #e9ecef;
          z-index: 1;
          transform: translateY(-50%);
        }
        
        .registration-progress-bar {
          height: 100%;
          background: linear-gradient(to right, #4CAF50, #8BC34A);
          transition: width 0.5s ease;
        }
        
        .registration-step-circles {
          display: flex;
          justify-content: space-between;
          position: relative;
          z-index: 2;
        }
        
        .step-circle {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background-color: white;
          border: 2px solid #e9ecef;
          color: #17695a;
         
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          position: relative;
          transition: all 0.3s ease;
          cursor: pointer;
          box-shadow: 0 4px 10px rgba(0,0,0,0.05);
        }
        
        .step-circle.active {
          background-color:#17695a;
          border-color: #4CAF50;
          color: white;
          box-shadow: 0 6px 15px rgba(76, 175, 80, 0.3);
          transform: scale(1.1);
        }
        
        .step-circle.completed {
          background-color: #4CAF50;
          border-color: #17695a
          color: white;
        }
        
        .step-label {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-top: 10px;
          font-size: 14px;
          font-weight: 500;
          color: #6c757d;
          white-space: nowrap;
          transition: all 0.3s ease;
        }
        
        .step-circle.active .step-label {
          color: #4CAF50;
          font-weight: bold;
        }

        /* Form Card Styles */
        .form-card-container {
          position: relative;
          z-index: 3;
        }
        
        .form-card {
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 15px 50px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
        }
        
        .registration-sidebar {
          position: relative;
          background-color: #4CAF50;
          min-height: 100%;
        }
        
        .sidebar-gradient {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        
        background: linear-gradient(135deg, #0f766e, #134e4a);
          opacity: 0.9;
        }
        
        .sidebar-content {
          position: relative;
          padding: 40px;
          color: white;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        
        .step-icon-large {
          width: 70px;
          height: 70px;
          background: rgba(255,255,255,0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
        }
        
        .sidebar-testimonial {
          background: rgba(0,0,0,0.1);
          border-radius: 16px;
          padding: 20px;
          margin-top: auto;
        }
        
        .testimonial-text {
          font-style: italic;
          margin-bottom: 15px;
          line-height: 1.6;
        }
        
        .testimonial-author {
          display: flex;
          align-items: center;
        }
        
        .author-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          font-weight: bold;
        }
        
        .author-name {
          font-weight: bold;
          font-size: 14px;
        }
        
        .author-role {
          font-size: 12px;
          opacity: 0.8;
        }

        /* Form Content Styles */
        .form-container {
          transition: opacity 0.3s ease;
        }
        
        .form-container.fade-animation {
          opacity: 0.6;
        }
        
        .step-header {
          margin-bottom: 30px;
        }
        
        .form-floating label {
          color: #6c757d;
        }
        
        .form-floating .form-control:focus + label {
          color: #4CAF50;
        }
        
        .form-control:focus, .form-select:focus {
          border-color: #4CAF50;
          box-shadow: 0 0 0 0.25rem rgba(76, 175, 80, 0.25);
        }
        
        .description-textarea {
          min-height: 120px;
        }
        
        .info-callout {
          display: flex;
          background-color: #f8f9fa;
          border-radius: 16px;
          padding: 20px;
          margin-top: 30px;
        }
        
        .callout-icon {
          width: 48px;
          height: 48px;
          background-color: rgba(76, 175, 80, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #4CAF50;
          margin-right: 20px;
          flex-shrink: 0;
        }
        
        .callout-list {
          padding-left: 20px;
          margin-bottom: 0;
        }
        
        .callout-list li {
          margin-bottom: 8px;
        }
        
        .map-container {
          position: relative;
          height: 200px;
          border-radius: 16px;
          overflow: hidden;
        }
        
        .map-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .map-pin {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 2;
          animation: bounce 2s infinite;
        }
        
        .pin {
          width: 20px;
          height: 20px;
          background-color: #f44336;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          margin-left: -2px;
          margin-top: -20px;
          box-shadow: 0 3px 10px rgba(0,0,0,0.2);
        }
        
        .pin-shadow {
          width: 16px;
          height: 6px;
          background: rgba(0,0,0,0.2);
          border-radius: 50%;
          margin: 6px auto 0;
          transform: translateX(-2px);
          filter: blur(2px);
        }
        
        .map-footer {
          background-color: #f8f9fa;
          padding: 12px;
          border-radius: 0 0 16px 16px;
          font-size: 14px;
          color: #6c757d;
        }

        /* Features Section */
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 15px;
        }
        
        .feature-item {
          position: relative;
        }
        
        .feature-checkbox {
          position: absolute;
          opacity: 0;
          width: 0;
          height: 0;
        }
        
        .feature-label {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          background-color: #f8f9fa;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .feature-checkbox:checked + .feature-label {
          background-color: #e8f5e9;
          border: 1px solid #4CAF50;
          color: #4CAF50;
        }
        
        .feature-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background-color: rgba(76, 175, 80, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          color: #4CAF50;
          transition: all 0.2s ease;
        }
        
        .feature-checkbox:checked + .feature-label .feature-icon {
          background-color: #4CAF50;
          color: white;
        }

        /* Hours Section */
        .hours-container {
          max-height: 320px;
          overflow-y: auto;
        }
        
        .hours-row {
          display: flex;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #e9ecef;
        }
        
        .day-label {
          width: 100px;
          font-weight: 500;
        }
        
        .hours-controls {
          display: flex;
          align-items: center;
          flex-grow: 1;
        }
        
        .form-switch {
          margin-right: 20px;
          min-width: 60px;
        }
        
        .time-inputs {
          display: flex;
          align-items: center;
          flex-grow: 1;
        }
        
        .time-input {
          width: 130px;
        }
        
        .time-separator {
          margin: 0 15px;
          color: #6c757d;
        }

        /* Upload Sections */
        .upload-card {
          border: 2px dashed #dee2e6;
          border-radius: 16px;
          transition: all 0.3s ease;
          overflow: hidden;
        }
        
        .dropzone-content {
          padding: 40px 20px;
          text-align: center;
        }
        
        .upload-icon-container {
          width: 80px;
          height: 80px;
          background: rgba(76, 175, 80, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        }
        
        .upload-icon {
          color: #4CAF50;
        }
        
        .upload-btn {
          margin-top: 15px;
          padding: 10px 30px;
          border-radius: 30px;
        }
        
        .dropzone:hover, .menu-dropzone:hover {
          border-color: #4CAF50;
          background-color: rgba(76, 175, 80, 0.05);
        }
        
        .image-gallery {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 15px;
        }
        
        .gallery-item {
          position: relative;
        }
        
        .image-card {
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
        }
        
        .image-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.15);
        }
        
        .gallery-img {
          width: 100%;
          height: 150px;
          object-fit: cover;
        }
        
        .btn-remove {
          position: absolute;
          top: 5px;
          right: 5px;
          width: 24px;
          height: 24px;
          background-color: rgba(220, 53, 69, 0.8);
          color: white;
          border: none;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          cursor: pointer;
          z-index: 2;
          line-height: 1;
          padding: 0;
        }
        
        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
          padding: 15px 10px 10px;
          color: white;
        }
        
        .image-name {
          font-size: 12px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .photo-tips {
          background-color: #f8f9fa;
          border-radius: 12px;
          padding: 15px;
        }
        
        .tips-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        
        .tip-item {
          display: flex;
          align-items: center;
        }
        
        .tip-icon {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background-color: rgba(76, 175, 80, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 10px;
          color: #4CAF50;
          flex-shrink: 0;
        }
        
        .menu-file-card {
          display: flex;
          align-items: center;
          background-color: #e8f5e9;
          border-radius: 12px;
          padding: 15px;
        }
        
        .file-icon {
          width: 48px;
          height: 48px;
          background-color: #4CAF50;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 15px;
          color: white;
        }
        
        .file-info {
          flex-grow: 1;
        }
        
        .file-name {
          font-weight: 500;
          margin-bottom: 3px;
        }
        
        .file-type {
          font-size: 12px;
          color: #6c757d;
        }
        
        .terms-check {
          padding: 15px;
          background-color: #f8f9fa;
          border-radius: 12px;
        }

        /* Form Navigation */
        .form-navigation {
          display: flex;
          justify-content: space-between;
        }
        
        .back-btn, .next-btn, .submit-btn {
          min-width: 160px;
          padding: 12px 24px;
          border-radius: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          box-shadow: 0 4px 10px rgba(0,0,0,0.1);
        }
        
        .next-btn, .submit-btn {
          margin-left: auto;
        }
        
        .back-btn:hover {
          background-color: #f8f9fa;
        }
        
        .next-btn:hover {
          box-shadow: 0 6px 15px rgba(13, 110, 253, 0.3);
          transform: translateY(-2px);
        }
        
        .submit-btn:hover {
          box-shadow: 0 6px 15px rgba(40, 167, 69, 0.3);
          transform: translateY(-2px);
        }

        /* Benefits Section */
        .registration-benefits {
          padding: 80px 0;
          margin-top: 60px;
        }
        
        .benefit-card {
          background-color: white;
          border-radius: 20px;
          padding: 30px;
          text-align: center;
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
          height: 100%;
          transition: all 0.3s ease;
        }
        
        .benefit-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 15px 40px rgba(0,0,0,0.1);
        }
        
        .benefit-icon {
          width: 80px;
          height: 80px;
          background: #17695a;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          color: white;
        }
        
        .benefit-card h4 {
          margin-bottom: 15px;
          font-weight: bold;
        }
        
        .benefit-card p {
          color: #6c757d;
          font-size: 16px;
          line-height: 1.6;
        }

        /* Success Page Styles */
        .success-page {
          overflow-x: hidden;
        }
        
        .success-banner {
          height: 300px;
          position: relative;
          background-color: #4CAF50;
        }
        
        .success-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: linear-gradient(135deg, rgba(76, 175, 80, 0.8), rgba(139, 195, 74, 0.8)), url('https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2074');
          background-size: cover;
          background-position: center;
        }
        
        .animated-checkmark {
          animation: scaleIn 0.5s ease-out;
        }
        
        .checkmark-circle {
          stroke-dasharray: 340;
          stroke-dashoffset: 340;
          animation: drawCircle 1s ease-out forwards;
        }
        
        .checkmark-check {
          stroke-dasharray: 80;
          stroke-dashoffset: 80;
          animation: drawCheck 1s 0.5s ease-out forwards;
        }
        
        .success-card {
          margin-top: -80px;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,0.1);
        }
        
        .success-restaurant-images {
          overflow: hidden;
        }
        
        .image-grid {
          display: flex;
          height: 250px;
        }
        
        .main-image {
          flex: 2;
          overflow: hidden;
        }
        
        .main-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        
        .main-image img:hover {
          transform: scale(1.05);
        }
        
        .side-images {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        
        .side-images img {
          flex: 1;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        
        .side-images img:hover {
          transform: scale(1.05);
        }
        
        .success-details {
          padding: 20px 0;
        }
        
        .next-steps {
          margin: 30px 0;
        }
        
        .next-step-item {
          display: flex;
          align-items: flex-start;
          margin-bottom: 25px;
          text-align: left;
          animation: fadeInRight 0.5s ease-out;
          animation-fill-mode: both;
        }
        
        .next-step-item:nth-child(2) {
          animation-delay: 0.3s;
        }
        
        .next-step-item:nth-child(3) {
          animation-delay: 0.6s;
        }
        
        .step-number {
          width: 36px;
          height: 36px;
          background-color: #4CAF50;
          border-radius: 50%;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          margin-right: 16px;
          flex-shrink: 0;
        }
        
        .step-content h5 {
          margin-bottom: 5px;
          font-weight: bold;
        }
        
        .step-content p {
          color: #6c757d;
          margin-bottom: 0;
        }
        
        .success-actions {
          animation: fadeInUp 0.8s ease-out;
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
          100% {
            transform: translateY(0px);
          }
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translate(-50%, -50%);
          }
          50% {
            transform: translate(-50%, -60%);
          }
        }
        
        @keyframes scaleIn {
          from {
            transform: scale(0);
          }
          to {
            transform: scale(1);
          }
        }
        
        @keyframes drawCircle {
          to {
            stroke-dashoffset: 0;
          }
        }
        
        @keyframes drawCheck {
          to {
            stroke-dashoffset: 0;
          }
        }

        /* Responsive Adjustments */
        @media (max-width: 991.98px) {
          .form-navigation {
            flex-direction: column;
            gap: 15px;
          }
          
          .back-btn, .next-btn, .submit-btn {
            width: 100%;
          }
          
          .next-btn, .submit-btn {
            margin-left: 0;
          }
        }
        
        @media (max-width: 767.98px) {
          .registration-banner {
            padding: 60px 0;
          }
          
          .features-grid {
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          }
          
          .tips-grid {
            grid-template-columns: 1fr;
          }
          
          .step-label {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default RegisterRestaurant;